import {FixedSizeVirtualList} from '@github-ui/ref-selector/fixed-size-virtual-list'
import {ActionList} from '@primer/react'

import type {User} from './user-types'
import {UserItem} from './UserItem'

import styles from './UsersList.module.css'

interface UsersListProps {
  users: User[]
  currentUser?: User
  onSelect?: (user: User) => void
  style?: React.CSSProperties // style to apply to the list container
  className?: string // additional className to apply to the list container
}

/**
 * The virtualized list has a fixed height. If we don't have very many users,
 * don't virtualize the list so that the container can shrink.
 */
const virtualizationThreshold = 20

export function UsersList(props: UsersListProps) {
  return props.users.length > virtualizationThreshold ? <VirtualUsersList {...props} /> : <FullUsersList {...props} />
}

/**
 * Non-virtual implementation
 */
function FullUsersList({users, currentUser, onSelect, style, className}: UsersListProps) {
  const combinedClassName = className ? `${styles.scrollableUserList} ${className}` : styles.scrollableUserList
  return (
    <ActionList role="menu" style={style} className={combinedClassName} selectionVariant="single">
      {users.map((user, index) => (
        <UserItem
          key={user.login}
          user={user}
          isCurrent={user.login === currentUser?.login}
          onSelect={onSelect}
          count={users.length}
          position={index}
        />
      ))}
    </ActionList>
  )
}

function VirtualUsersList({users, currentUser, onSelect, style, className}: UsersListProps) {
  const combinedClassName = className ? `${styles.scrollableUserList} ${className}` : styles.scrollableUserList
  return (
    <FixedSizeVirtualList
      items={users}
      itemHeight={33}
      style={style}
      makeKey={user => user.login}
      shouldUseActionList
      renderItem={(user: User, index: number) => (
        <UserItem
          key={user.login}
          user={user}
          isCurrent={user.login === currentUser?.login}
          onSelect={onSelect}
          count={users.length}
          position={index}
        />
      )}
      className={combinedClassName}
    />
  )
}
